/* eslint-disable react-hooks/rules-of-hooks */
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import {
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Box,
  Tooltip,
  Button,
  ButtonGroup,
  useMediaQuery,
  Switch,
  Table as MuiTable,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Typography,
  IconButton,
  TextField,
  InputAdornment
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  IFieldWalkthroughDetail,
  IFieldWalkthroughDetailItem,
  IFieldWalkthroughLocation,
  IFieldWalkthroughDefaultMarkup,
  UserRoles,
  FieldWalkthroughResponse
} from '../../../models';
import {
  getFieldWalkthroughDetail,
  updateFieldWalkthrough,
  updateFieldWalkthroughItemPhoto,
  FIELD_WALKTHROUGH_DEFAULT_MARKUPS_QUERY_KEY,
  getFieldWalkthroughDefaultMarkups
} from '../../../fetch';
import { scrollToTop, formatCapitalCase } from '../../../helpers';
import {
  EditableCell,
  ImageUpload,
  ImageModal,
  FieldWalkthroughLocationDropdown,
  PageTitleWithButton,
  Loader
} from '../../../components';
import {
  Add,
  UnfoldLess,
  UnfoldMore,
  CheckBox,
  CheckBoxOutlineBlank,
  DriveFileRenameOutline,
  Search,
  Close
} from '@mui/icons-material';
import { MobileFieldWalkthroughItem } from './mobile-fieldwalkthroughs-item';
import { AssignToModal } from './assign-to-modal';
import { UserContext } from '../../../context';
import { VariableSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import React, { useState, useContext, useMemo, useCallback, useEffect, useRef } from 'react';
import { DeficiencyReportButton } from './deficiency-report-button';
import { ConsolidatedFloorplanButton } from './consolidated-floorplan-button';
import { FloorplanModal } from './floorplan-modal';
import { useFlags } from '../../../hooks';
import { AddWalkthroughItemsModal } from './add-walkthrough-items-modal';
import { VerifyCompleteModal } from './verify-complete-modal';

const columns = [
  {
    Header: 'Name',
    accessor: 'text',
    overrideWidth: 490
  },
  {
    Header: 'N/A',
    accessor: 'notApplicableMarked',
    overrideWidth: 70
  },
  {
    Header: 'Notes',
    accessor: 'notes',
    sort: false,
    overrideWidth: 200
  },
  {
    Header: 'Photo',
    accessor: 'photo'
  },
  {
    Header: 'Foreman Response',
    accessor: 'foremanResponse',
    overrideWidth: 225
  },
  {
    Header: 'Supervisor Response',
    accessor: 'supervisorResponse',
    overrideWidth: 225
  },
  {
    Header: 'PM Response',
    accessor: 'projectManagerResponse',
    overrideWidth: 225
  }
];

const ALLOWED_SEARCH_AND_ADD_ROLES = [
  UserRoles.BLSuperintendent2,
  UserRoles.BLSuperintendent3,
  UserRoles.BLSuperintendent4,
  UserRoles.BLProjectManager,
  UserRoles.BLProjectmanager1,
  UserRoles.BLProjectmanager2,
  UserRoles.BLProjectmanager3
];

export const FieldWalkthroughsDetails = () => {
  const { user, isPM, isPMOrSuperintendent, isSupervisor, isForeman } = useContext(UserContext);
  const queryString = window.location.search;

  const isAdmin = user?.userRoles?.includes(UserRoles.BLAdmin);
  const isFieldAdmin = user?.userRoles?.includes(UserRoles.BLFieldAdmin);
  const canSearchAndAddItems = user?.userRoles?.some(role =>
    ALLOWED_SEARCH_AND_ADD_ROLES.includes(role as UserRoles)
  );
  const classes = useStyles();
  const isMobile = useMediaQuery('(max-width: 1380px)');
  const isMdMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const isSmMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { fieldWalkthroughId } = useParams<{ fieldWalkthroughId: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const [locations, setLocations] = useState<IFieldWalkthroughLocation[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | string>('');
  const [isFirst, setIsFirst] = useState(false);
  const [isLast, setIsLast] = useState(false);
  const [currentImage, setCurrentImage] = useState<string>('');
  const [isSavingImage, setSavingImage] = useState<{
    [id: number]: boolean;
  }>({});
  const [isShowingFloorPlanModal, setIsShowingFloorPLanModal] = useState<{
    [id: number]: boolean;
  }>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isAddingToPunchlist, setIsAddingToPunchlist] = useState<boolean>(false);
  const [isShowingAssignToModal, showAssignToModal] = useState<{
    [type: string]: boolean;
  }>({});
  const [walkthroughItems, setWalkthroughItems] = useState<IFieldWalkthroughDetailItem[] | null>(
    null
  );
  const [walkthrough, setWalkthrough] = useState<IFieldWalkthroughDetail | null>(null);
  const [selectedDeficientItems, setSelectedDeficientItems] = useState<
    IFieldWalkthroughDetailItem[]
  >([]);
  const [changedWalkthroughItems, setChangedWalkthroughItems] = useState<
    IFieldWalkthroughDetailItem[]
  >([]);
  const [hideMeetsStandards, toggleHideMeetsStandards] = useState<boolean>(
    isPMOrSuperintendent ? true : false
  );
  const { fwtPlanMarkup: fwtPlanMarkupFF } = useFlags();
  const [isLocationDropdownLoading, setIsLocationDropdownLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isShowingAddItemsModal, setIsShowingAddItemsModal] = useState(false);
  const queryClient = useQueryClient();
  const { isLoading: isFieldWalkthroughLoading, refetch: refetchFieldWalkthroughDetail } = useQuery<
    IFieldWalkthroughDetail,
    Error
  >(
    ['field-walkthrough-data', fieldWalkthroughId, selectedLocationId],
    () => getFieldWalkthroughDetail(fieldWalkthroughId, +selectedLocationId),
    {
      enabled: typeof selectedLocationId === 'number',
      notifyOnChangeProps: 'tracked',
      onSuccess: d => {
        setWalkthrough(d);
        // check here if re-call the endpoint and had made selections previously, find the value and update
        // the walkthrough items with the updated data
        if (changedWalkthroughItems?.length > 0) {
          const items = d.items.map(item => {
            const foundWalkthroughItemIndex = changedWalkthroughItems?.findIndex(
              c => c.fieldWalkthroughItemId === item.fieldWalkthroughItemId
            );
            if (foundWalkthroughItemIndex >= 0) {
              return {
                ...changedWalkthroughItems[foundWalkthroughItemIndex],
                // make sure we take what comes back from the api for markup files otherwise we might lose them
                // if the user makes changes on the page and then opens the floor plan modal and edits and saves a floor plan
                markupFiles: item.markupFiles
              };
            }
            return item;
          });
          setWalkthroughItems(items);
        } else {
          setWalkthroughItems(d.items);
        }
        setExpandedItems({});
        setIsExpandAll(false);
      }
    }
  );

  const { isLoading: isLoadingDefaultFloorPlans, data: defaultFloorPlans } = useQuery<
    IFieldWalkthroughDefaultMarkup[],
    Error
  >(
    [FIELD_WALKTHROUGH_DEFAULT_MARKUPS_QUERY_KEY],
    () => getFieldWalkthroughDefaultMarkups(fieldWalkthroughId),
    {
      enabled: !!fieldWalkthroughId
    }
  );

  const hasCreatedPunchlist = useMemo(() => !!walkthrough?.punchlistId, [walkthrough]);
  const newItemsToAssign = useMemo(
    () => selectedDeficientItems?.filter(item => !item.hasPunchlistItem),

    [selectedDeficientItems]
  );
  const hasItemsToAssign = useMemo(() => newItemsToAssign?.length > 0, [newItemsToAssign]);

  const handleChangedItems = (item: IFieldWalkthroughDetailItem) => {
    const foundWalkthroughItemIndex = changedWalkthroughItems?.findIndex(
      c => c.fieldWalkthroughItemId === item.fieldWalkthroughItemId
    );
    if (foundWalkthroughItemIndex >= 0) {
      const copy = JSON.parse(JSON.stringify([...changedWalkthroughItems]));
      copy[foundWalkthroughItemIndex] = item;
      setChangedWalkthroughItems(copy);
    } else {
      setChangedWalkthroughItems([...changedWalkthroughItems, item]);
    }
  };

  const isItemDeficient = (item: IFieldWalkthroughDetailItem) => {
    // Refactored to be more readable and more explicit and catch more edge cases
    // If the Project Manager Response is Repair / Replace
    if (item.projectManagerResponse === FieldWalkthroughResponse.RepairReplace) {
      return true;
    }
    // If the Supervisor Response is Repair / Replace and the Project Manager Response is not set
    if (
      item.supervisorResponse === FieldWalkthroughResponse.RepairReplace &&
      (!item.projectManagerResponse ||
        item.supervisorResponse === FieldWalkthroughResponse.RepairReplace) &&
      (!item.foremanResponse || item.foremanResponse === FieldWalkthroughResponse.MeetsStandards)
    ) {
      return true;
    }
    // If the Project Manager Response is not set and the Supervisor Response is not set and the Foreman Response is Repair / Replace
    if (
      !item.projectManagerResponse &&
      (!item.supervisorResponse ||
        item.supervisorResponse === FieldWalkthroughResponse.RepairReplace) &&
      item.foremanResponse === FieldWalkthroughResponse.RepairReplace
    ) {
      return true;
    }
    return false;
  };
  const handleDeficientItems = (item: IFieldWalkthroughDetailItem) => {
    const ids = selectedDeficientItems.map(item => item.fieldWalkthroughItemId);

    // item is deficient and should be added to the deficient list
    if (isItemDeficient(item) && !ids.includes(item.fieldWalkthroughItemId)) {
      setSelectedDeficientItems([...selectedDeficientItems, item]);
      // item is deficient and still needs to be in the deficient list
    } else if (isItemDeficient(item) && ids.includes(item.fieldWalkthroughItemId)) {
      return;
      // item is not deficient and needs to be removed from the deficient list
    } else {
      setSelectedDeficientItems(
        selectedDeficientItems.filter(
          walkthroughItem => walkthroughItem.fieldWalkthroughItemId !== item.fieldWalkthroughItemId
        )
      );
    }
  };

  const updateItems = (d: IFieldWalkthroughDetailItem, columnId: string, value: any) => {
    const data: IFieldWalkthroughDetailItem = {
      ...d,
      [columnId]: value
    };
    handleDeficientItems(data);
    handleChangedItems(data);
    setWalkthroughItems(old => {
      return old.map((row, index) => {
        if (d.fieldWalkthroughItemId === row.fieldWalkthroughItemId) {
          return data;
        }
        return row;
      });
    });
  };

  const handleImageUpload = async (
    file: File,
    locationItem: IFieldWalkthroughDetailItem,
    index: number
  ) => {
    try {
      setSavingImage({
        [locationItem.fieldWalkthroughItemId]: true
      });

      await updateFieldWalkthroughItemPhoto(
        fieldWalkthroughId,
        locationItem.fieldWalkthroughItemId,
        file
      );
      enqueueSnackbar(`Image saved!`, {
        variant: 'success'
      });
      updateItems(locationItem, 'hasPhoto', true);
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error uploading image. Please try again.`, {
        variant: 'error'
      });
    } finally {
      setSavingImage({
        [locationItem.fieldWalkthroughItemId]: false
      });
    }
  };

  const verifyComplete = async () => {
    setIsShowingVerifyCompleteModal(true);
  };

  const consolidateDeficientItemsAndChangedItems = () => {
    selectedDeficientItems.find(item => changedWalkthroughItems.includes(item));
    const items = [...selectedDeficientItems, ...changedWalkthroughItems];
    const uniqueItems = Array.from(new Set(items));
    setChangedWalkthroughItems(uniqueItems);
  };

  const handleSave = useCallback(async () => {
    selectedDeficientItems.find(item => changedWalkthroughItems.includes(item));

    consolidateDeficientItemsAndChangedItems();
    try {
      if (selectedDeficientItems.length > 0) {
        changedWalkthroughItems.find(item => selectedDeficientItems.includes(item));
      }
      setIsSaving(true);
      await updateFieldWalkthrough(fieldWalkthroughId, {
        items: changedWalkthroughItems
      });
      enqueueSnackbar('Saved successfully', {
        variant: 'success'
      });
      setChangedWalkthroughItems([]);
      setSelectedDeficientItems([]);
      await refetchFieldWalkthroughDetail();
      return true;
    } catch (error: any) {
      enqueueSnackbar(error?.Detail ?? 'Error saving field walkthrough', {
        variant: 'error'
      });
      return false;
    } finally {
      setIsSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walkthroughItems]);

  const handleSaveAndBack = useCallback(async () => {
    if (!(await handleSave())) {
      return;
    }
    const currentOptionIndex = locations.findIndex(
      o => o.fieldWalkthroughLocationID === +selectedLocationId
    );
    const previousIndex = currentOptionIndex - 1;
    setIsFirst(previousIndex === 0);
    setIsLast(false);
    setSelectedLocationId(locations[previousIndex].fieldWalkthroughLocationID);
    scrollToTop();
  }, [handleSave, selectedLocationId, locations]);

  const handleSaveAndContinue = useCallback(async () => {
    if (!(await handleSave())) {
      return;
    }
    const currentOptionIndex = locations.findIndex(
      o => o.fieldWalkthroughLocationID === +selectedLocationId
    );
    const nextIndex = currentOptionIndex + 1;
    setIsFirst(false);
    setIsLast(nextIndex === locations.length - 1);
    setSelectedLocationId(locations[nextIndex].fieldWalkthroughLocationID);
    scrollToTop();
  }, [handleSave, selectedLocationId, locations]);

  const handleAddToPunchlist = async () => {
    try {
      setIsAddingToPunchlist(true);
      // save walkthrough which merges into punchlist
      await updateFieldWalkthrough(fieldWalkthroughId, {
        items: selectedDeficientItems
      });
      enqueueSnackbar(`Success, punchlist updated!`, {
        variant: 'success'
      });
      setSelectedDeficientItems([]);
      setChangedWalkthroughItems([]);
      await refetchFieldWalkthroughDetail();
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'Error updating punchlist, please try again.', {
        variant: 'error'
      });
    } finally {
      setIsAddingToPunchlist(false);
    }
  };

  let showPageLoader = isSaving || isCompleting;
  let pageLoaderMessager = 'Loading...';
  if (showPageLoader) {
    if (isCompleting) {
      pageLoaderMessager = 'Completing report...';
    }
    if (isSaving) {
      pageLoaderMessager = 'Saving report...';
    }
  }

  const filterWalkthroughItems = useMemo(() => {
    if (walkthroughItems) {
      return walkthroughItems
        .map(item => {
          if (item.projectManagerResponse === 'Meets Standards') {
            return null;
          }
          if (
            item.supervisorResponse === 'Meets Standards' &&
            item.projectManagerResponse === null
          ) {
            return null;
          }
          if (item.notApplicableMarked) {
            return null;
          }
          return item;
        })
        .filter(Boolean);
    }
    return [];
  }, [walkthroughItems]);

  const filteredItems = useMemo(() => {
    const itemsToFilter = hideMeetsStandards ? filterWalkthroughItems : walkthroughItems || [];

    if (!searchTerm.trim()) {
      return itemsToFilter;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return itemsToFilter.filter(
      item =>
        item.text?.toLowerCase().includes(lowerSearchTerm) ||
        item.notes?.toLowerCase().includes(lowerSearchTerm)
    );
  }, [searchTerm, hideMeetsStandards, filterWalkthroughItems, walkthroughItems]);

  const hasInvalidData =
    !walkthrough ||
    typeof walkthrough.neighborhoodName === 'undefined' ||
    walkthrough.neighborhoodName === null ||
    typeof walkthrough.lotNumber === 'undefined' ||
    walkthrough.lotNumber === null ||
    typeof walkthrough.block === 'undefined' ||
    walkthrough.block === null ||
    typeof walkthrough.neighborhoodPhase === 'undefined' ||
    walkthrough.neighborhoodPhase === null;

  const [isAllowedToVerify, setIsAllowedToVerify] = useState<boolean>(
    changedWalkthroughItems?.length === 0 && selectedDeficientItems?.length === 0
  );

  useEffect(() => {
    setIsAllowedToVerify(
      changedWalkthroughItems?.length === 0 && selectedDeficientItems?.length === 0
    );
  }, [changedWalkthroughItems, selectedDeficientItems]);

  let listRef = React.createRef<VariableSizeList>();

  const resetItemHeights = (index: number) => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(index);
    }
  };

  const itemHeights = useRef({});
  const setItemHeights = useCallback(
    (index: number, height: number) => {
      itemHeights.current = { ...itemHeights.current, [index]: height };
      setTimeout(() => {
        resetItemHeights(0);
      }, 50);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const defaultHeight = useMemo(() => (isMobile ? 50 : 110), [isMobile]);
  const getItemHeight = index => itemHeights.current[index] || defaultHeight;

  const [expandedItems, setExpandedItems] = useState<{
    [id: number]: boolean;
  }>({});
  const [isExpandAll, setIsExpandAll] = useState<boolean>(false);

  // Custom window resize event to force the list to recalculate its height for each item
  // NOTE: Fix for overlapping and odd excess spacing after window resize on both mobile and desktop
  window.addEventListener('resize', () => {
    setTimeout(() => {
      resetItemHeights(0);
    }, 50);
  });

  const [isShowingVerifyCompleteModal, setIsShowingVerifyCompleteModal] = useState(false);

  return (
    <Box>
      {(!!showPageLoader || isAddingToPunchlist) && (
        <Loader type="fullscreen" position="centered" title={pageLoaderMessager} />
      )}
      <PageTitleWithButton
        pageTitleBackProps={{
          backText: 'Back to field walkthroughs',
          backUrl: `/production/field-walkthroughs${queryString}`,
          title: 'Perform Field Walkthrough',
          subTitle: hasInvalidData
            ? ''
            : `${walkthrough.neighborhoodName} Lot ${walkthrough.lotNumber} Block ${walkthrough.block} Phase ${walkthrough.neighborhoodPhase}`
        }}
        button={
          <ButtonGroup
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                md: 'row'
              }
            }}
          >
            {isPM && (
              <ConsolidatedFloorplanButton
                walkthrough={walkthrough}
                fieldWalkthroughId={fieldWalkthroughId}
                isFieldWalkthroughLoading={isFieldWalkthroughLoading}
              />
            )}
            <DeficiencyReportButton
              walkthrough={walkthrough}
              fieldWalkthroughId={fieldWalkthroughId}
              isFieldWalkthroughLoading={isFieldWalkthroughLoading}
            />
            <LoadingButton
              variant="contained"
              startIcon={<Add />}
              disabled={
                isAddingToPunchlist ||
                isFieldWalkthroughLoading ||
                isLocationDropdownLoading ||
                !hasItemsToAssign ||
                walkthroughItems?.length === 0
              }
              loadingPosition="start"
              loading={isAddingToPunchlist}
              sx={{
                width: {
                  xs: '100%',
                  sm: 'auto'
                }
              }}
              onClick={() =>
                showAssignToModal({
                  punchlist: true
                })
              }
            >
              {isFieldWalkthroughLoading && isLocationDropdownLoading
                ? 'Loading...'
                : hasCreatedPunchlist
                ? 'Update Punchlist'
                : 'Add to Punchlist'}
            </LoadingButton>
          </ButtonGroup>
        }
      />
      {!isFieldWalkthroughLoading &&
      !isLocationDropdownLoading &&
      !walkthrough &&
      !walkthroughItems ? (
        <Typography align="center" mt={2}>
          Field Walkthrough does not exist.
        </Typography>
      ) : (
        <>
          <Box
            mb={isMdMobile ? 0 : 2}
            display="flex"
            alignItems="center"
            flexWrap="wrap"
            gap="8px"
            position="relative"
          >
            <Box
              flex={1}
              gap={0.5}
              display="flex"
              sx={{
                flexDirection: {
                  xs: 'column',
                  sm: 'row'
                }
              }}
            >
              <FieldWalkthroughLocationDropdown
                isLoadingDropdown={isLoading => setIsLocationDropdownLoading(isLoading)}
                fieldWalkthroughId={fieldWalkthroughId}
                isDisabled={false}
                hasAllLocationsOption={isPMOrSuperintendent}
                value={+selectedLocationId}
                onDataLoad={locationOptions => {
                  setLocations(locationOptions);
                }}
                onChange={(value, info) => {
                  setSelectedLocationId(value);
                  setIsFirst(info.isFirst);
                  setIsLast(info.isLast);
                }}
                showAll={hideMeetsStandards}
                sx={{
                  width: isMdMobile ? '100%' : 'auto'
                }}
              />
              {canSearchAndAddItems && (
                <TextField
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                  sx={{
                    width: {
                      xs: '100%',
                      sm: '300px'
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearchTerm('')}
                          sx={{ padding: 0 }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  disabled={isFieldWalkthroughLoading || isLocationDropdownLoading}
                />
              )}
            </Box>
            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              gap="8px"
              flexDirection={isMdMobile ? 'column' : 'row'}
              width={isMdMobile ? '100%' : 'auto'}
            >
              {isPMOrSuperintendent && (
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={hideMeetsStandards}
                      id="active-inactive"
                      disabled={isFieldWalkthroughLoading || isLocationDropdownLoading || isSaving}
                      onChange={(_, checked) => {
                        toggleHideMeetsStandards(checked);
                        // if turned off and on the location "All", switch to first location
                        if (!checked && selectedLocationId === -1) {
                          setSelectedLocationId(1);
                        }
                        // if turned on and not on the location "All", switch to all location
                        if (checked && selectedLocationId !== -1) {
                          setSelectedLocationId(-1);
                        }
                      }}
                    />
                  }
                  label="Hide Meets Standards"
                />
              )}
              <ButtonGroup
                sx={{
                  flexDirection: isSmMobile ? 'column' : 'row',
                  width: isSmMobile ? '100%' : 'auto',
                  paddingBottom: isSmMobile && selectedLocationId === -1 ? 1 : 0
                }}
              >
                {canSearchAndAddItems && (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => setIsShowingAddItemsModal(true)}
                    disabled={isFieldWalkthroughLoading || isLocationDropdownLoading}
                  >
                    Add Walkthrough Items
                  </Button>
                )}
                {(isPM || isSupervisor || isForeman) && selectedLocationId !== -1 && (
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={isFieldWalkthroughLoading || isLocationDropdownLoading}
                    onClick={() => {
                      const result = window.confirm(
                        `Are you sure you want to mark ${
                          isForeman
                            ? 'Foreman Response'
                            : isSupervisor
                            ? 'Supervisor Response'
                            : isPM
                            ? 'PM Response'
                            : ''
                        } items as meets standards?`
                      );
                      if (result) {
                        const data = walkthroughItems.map(item => ({
                          ...item,
                          foremanResponse: isForeman
                            ? FieldWalkthroughResponse.MeetsStandards
                            : item.foremanResponse,
                          supervisorResponse: isSupervisor
                            ? FieldWalkthroughResponse.MeetsStandards
                            : item.supervisorResponse,
                          projectManagerResponse: isPM
                            ? FieldWalkthroughResponse.MeetsStandards
                            : item.projectManagerResponse
                        }));
                        // Update UI to be all checked
                        setWalkthroughItems(data);
                        // Update Changed Values so they can be saved
                        setChangedWalkthroughItems(data);
                        enqueueSnackbar(`Items meets standards!`, {
                          variant: 'success'
                        });
                      }
                    }}
                  >
                    Select Meets Standards
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={isFieldWalkthroughLoading || isLocationDropdownLoading}
                  onClick={() => {
                    const data = walkthroughItems.map(item => ({
                      ...item,
                      notApplicableMarked: true
                    }));
                    // Update UI to be all checked
                    setWalkthroughItems(data);
                    // Update Changed Values so they can be saved
                    setChangedWalkthroughItems(data);
                    // Clean up deficient items, now that all are NA
                    setSelectedDeficientItems([]);
                    enqueueSnackbar(`Items marked as N/A!`, {
                      variant: 'success'
                    });
                  }}
                >
                  Mark All items N/A
                </Button>
              </ButtonGroup>
              {isMobile && selectedLocationId !== -1 && (
                <Box sx={{ alignSelf: 'end' }}>
                  <Tooltip title={`${isExpandAll ? 'Collapse' : 'Expand'} all items`}>
                    <IconButton
                      onClick={() => {
                        setExpandedItems({}); // Allow overriding already expanded/collapsed items when All toggle clicked
                        setIsExpandAll(!isExpandAll);
                      }}
                      disabled={
                        isFieldWalkthroughLoading ||
                        isLocationDropdownLoading ||
                        filterWalkthroughItems?.length === 0 ||
                        walkthroughItems?.length === 0
                      }
                    >
                      {isExpandAll ? <UnfoldLess /> : <UnfoldMore />}
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </Box>
          {(isFieldWalkthroughLoading || isLocationDropdownLoading) && (
            <Loader type="inline" position="centered" />
          )}
          {!isFieldWalkthroughLoading &&
            !isLocationDropdownLoading &&
            ((hideMeetsStandards && filterWalkthroughItems?.length === 0) ||
              walkthroughItems?.length === 0 ||
              (searchTerm.trim() !== '' && filteredItems?.length === 0)) && (
              <Typography align="center" mt={2}>
                No results, check your filters.
              </Typography>
            )}
          {!isFieldWalkthroughLoading && !isLocationDropdownLoading && (
            // Height takes into account the header height as well as content
            <TableContainer style={{ height: '752px' }}>
              <MuiTable className={classes.table} component="div" size="medium">
                {!isMobile && (
                  <TableHead component="div">
                    <TableRow component="div">
                      {columns.map((column: any, i) => {
                        return (
                          <TableCell
                            key={`header-cell-${i}`}
                            title={column.Header || ''}
                            sx={{
                              fontWeight: 'bold',
                              color: theme => theme.palette.typography.tableHeaders,
                              width: column.overrideWidth,
                              minWidth: column.Header.includes('Response')
                                ? column.overrideWidth
                                : 'inherit',

                              padding: column.padding ?? '8px',
                              paddingLeft: 0
                            }}
                            component="div"
                          >
                            {column.Header}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                )}
                <TableBody component="div" style={{ height: '700px', maxHeight: '700px' }}>
                  <AutoSizer style={{ height: '700px', maxHeight: '700px' }}>
                    {({ height, width }) => {
                      return (
                        <VariableSizeList
                          maxHeight={height}
                          height={height}
                          itemCount={filteredItems?.length || 0}
                          itemData={filteredItems ?? []}
                          estimatedItemSize={index => (isMobile ? getItemHeight(index) : 110)}
                          itemSize={index => (isMobile ? getItemHeight(index) : 110)}
                          width={width}
                          ref={listRef}
                          onItemsRendered={({ visibleStartIndex }) => {
                            if (listRef.current) {
                              listRef.current.resetAfterIndex(visibleStartIndex, true);
                            }
                          }}
                        >
                          {({ style, index, data }) => {
                            const item: IFieldWalkthroughDetailItem = data[index];
                            return (
                              <>
                                {!isMobile && item && (
                                  <TableRow style={style} component="div">
                                    <TableCell component="div" sx={{ width: 490 }}>
                                      <Box>
                                        <Typography variant="body1">{item.text}</Typography>
                                        {item?.hasPunchlistItem && !item?.notApplicableMarked && (
                                          <Box display="flex" alignItems="center">
                                            <Typography
                                              variant="caption"
                                              sx={{ mr: 0.25, color: 'gray' }}
                                            >
                                              Complete
                                            </Typography>
                                            {item.isPunchlistItemComplete ? (
                                              <CheckBox
                                                sx={{
                                                  color: 'green',
                                                  marginBottom: '4px',
                                                  fontSize: '18px'
                                                }}
                                              />
                                            ) : (
                                              <CheckBoxOutlineBlank
                                                sx={{
                                                  color: 'gray',
                                                  marginBottom: '4px',
                                                  fontSize: '18px'
                                                }}
                                              />
                                            )}
                                            <Typography
                                              variant="caption"
                                              sx={{ ml: 0.5, color: 'gray' }}
                                            >
                                              Assigned To:
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              sx={{ ml: 0.25, color: 'gray' }}
                                            >
                                              {formatCapitalCase(item.assignedToDisplayName)}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell component="div" sx={{ width: 70, padding: 0 }}>
                                      <FormControlLabel
                                        color="primary"
                                        sx={{ margin: 0 }}
                                        control={
                                          <Checkbox
                                            checked={item.notApplicableMarked}
                                            id="notApplicableMarked"
                                            sx={{ padding: 0 }}
                                            onChange={(_, checked) => {
                                              updateItems(item, 'notApplicableMarked', checked);
                                              // remove the selected item if the checkbox is checked
                                              setSelectedDeficientItems(
                                                selectedDeficientItems.filter(
                                                  walkthroughItem =>
                                                    walkthroughItem.fieldWalkthroughItemId !==
                                                    item.fieldWalkthroughItemId
                                                )
                                              );
                                            }}
                                          />
                                        }
                                        label=""
                                      />
                                    </TableCell>
                                    <TableCell component="div" sx={{ width: 200, paddingLeft: 0 }}>
                                      <EditableCell
                                        updateMyData={(original, id, value, rowIndex) =>
                                          updateItems(item, 'notes', value)
                                        }
                                        value={item.notes}
                                        row={item}
                                        column={{
                                          id: 'notes',
                                          Header: 'Notes'
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell component="div" sx={{ width: 145 }}>
                                      <Box display="flex" gap={0.5} alignItems="center">
                                        {item.hasPhoto && (
                                          <picture
                                            className={classes.pictureImages}
                                            onClick={() =>
                                              setCurrentImage(
                                                `${window.location.origin}/api/field-walkthroughs/${fieldWalkthroughId}/items/${item.fieldWalkthroughItemId}/image`
                                              )
                                            }
                                          >
                                            <img
                                              className={classes.colorImage}
                                              src={`${window.location.origin}/api/field-walkthroughs/${fieldWalkthroughId}/items/${item.fieldWalkthroughItemId}/image`}
                                              alt=""
                                            />
                                          </picture>
                                        )}
                                        <Tooltip title="Upload Image" placement="top">
                                          <div>
                                            <ImageUpload
                                              imageKey={`${index}`}
                                              showSource={false}
                                              disabled={isSavingImage[item.fieldWalkthroughItemId]}
                                              showLabel={false}
                                              iconUploadButtonSize="medium"
                                              disableIconButtonPadding
                                              handleFileChange={(_, file) =>
                                                handleImageUpload(file, item, index)
                                              }
                                            />
                                          </div>
                                        </Tooltip>
                                        {walkthrough.hasAvailableMarkups && fwtPlanMarkupFF && (
                                          <Tooltip
                                            title={`${
                                              item.markupFiles.length > 0 ? 'Edit' : ''
                                            } Floorplan markup`}
                                            placement="top"
                                          >
                                            <div>
                                              <IconButton
                                                color={
                                                  item.markupFiles.length > 0
                                                    ? 'secondary'
                                                    : 'primary'
                                                }
                                                size="large"
                                                sx={{ padding: 0 }}
                                                onClick={() => {
                                                  setIsShowingFloorPLanModal({
                                                    [item.fieldWalkthroughItemId]: true
                                                  });
                                                }}
                                              >
                                                <DriveFileRenameOutline />
                                              </IconButton>
                                            </div>
                                          </Tooltip>
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell component="div" sx={{ minWidth: 225 }}>
                                      <FormControl variant="outlined" size="medium" required>
                                        <RadioGroup
                                          value={item.foremanResponse}
                                          onChange={e => {
                                            updateItems(item, 'foremanResponse', e.target.value);
                                          }}
                                          aria-label="foremanResponse"
                                          name="foremanResponse"
                                        >
                                          <FormControlLabel
                                            value="Meets Standards"
                                            control={<Radio />}
                                            label="Meets Standards"
                                            classes={{
                                              label: classes.label
                                            }}
                                            disabled={item.notApplicableMarked}
                                          />
                                          <FormControlLabel
                                            value="Repair / Replace"
                                            control={<Radio />}
                                            label="Repair / Replace"
                                            classes={{
                                              label: classes.label
                                            }}
                                            disabled={item.notApplicableMarked}
                                          />
                                        </RadioGroup>
                                      </FormControl>
                                    </TableCell>
                                    <TableCell component="div" sx={{ minWidth: 225 }}>
                                      <FormControl
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        required
                                      >
                                        <RadioGroup
                                          value={item.supervisorResponse}
                                          onChange={e => {
                                            updateItems(item, 'supervisorResponse', e.target.value);
                                          }}
                                          aria-label="supervisorResponse"
                                          name="supervisorResponse"
                                        >
                                          <FormControlLabel
                                            value="Meets Standards"
                                            control={<Radio />}
                                            label="Meets Standards"
                                            disabled={item.notApplicableMarked}
                                            classes={{
                                              label: classes.label
                                            }}
                                          />
                                          <FormControlLabel
                                            value="Repair / Replace"
                                            control={<Radio />}
                                            label="Repair / Replace"
                                            disabled={item.notApplicableMarked}
                                            classes={{
                                              label: classes.label
                                            }}
                                          />
                                        </RadioGroup>
                                      </FormControl>
                                    </TableCell>
                                    <TableCell component="div" sx={{ minWidth: 225 }}>
                                      <FormControl
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        required={
                                          item.foremanResponse === 'Meets Standards' ||
                                          item.supervisorResponse === 'Meets Standards'
                                            ? false
                                            : true
                                        }
                                      >
                                        <RadioGroup
                                          value={item.projectManagerResponse}
                                          onChange={e => {
                                            updateItems(
                                              item,
                                              'projectManagerResponse',
                                              e.target.value
                                            );
                                          }}
                                          aria-label="projectManagerResponse"
                                          name="projectManagerResponse"
                                        >
                                          <FormControlLabel
                                            value="Meets Standards"
                                            control={<Radio />}
                                            label="Meets Standards"
                                            disabled={item.notApplicableMarked}
                                            classes={{
                                              label: classes.label
                                            }}
                                          />
                                          <FormControlLabel
                                            value="Repair / Replace"
                                            control={<Radio />}
                                            label="Repair / Replace"
                                            disabled={item.notApplicableMarked}
                                            classes={{
                                              label: classes.label
                                            }}
                                          />
                                        </RadioGroup>
                                      </FormControl>
                                    </TableCell>
                                  </TableRow>
                                )}
                                {isMobile && item && (
                                  <TableRow style={style} component="div">
                                    <MobileFieldWalkthroughItem
                                      original={item}
                                      index={index}
                                      updateMyData={updateItems}
                                      setCurrentImage={(val: string) => setCurrentImage(val)}
                                      fieldWalkthroughId={fieldWalkthroughId}
                                      handleImageUpload={handleImageUpload}
                                      isSavingImage={isSavingImage}
                                      itemHeights={itemHeights}
                                      setItemHeights={setItemHeights}
                                      resetItemHeights={resetItemHeights}
                                      expandedItems={expandedItems}
                                      setExpandedItems={setExpandedItems}
                                      selectedLocationId={+selectedLocationId}
                                      isExpandAll={isExpandAll}
                                      selectedDeficientItems={selectedDeficientItems}
                                      setSelectedDeficientItems={setSelectedDeficientItems}
                                      handleFlooplanMarkup={
                                        walkthrough.hasAvailableMarkups && fwtPlanMarkupFF
                                          ? () => {
                                              setIsShowingFloorPLanModal({
                                                [item.fieldWalkthroughItemId]: true
                                              });
                                            }
                                          : undefined
                                      }
                                    />
                                  </TableRow>
                                )}
                                {isShowingFloorPlanModal[item.fieldWalkthroughItemId] && (
                                  <FloorplanModal
                                    isOpen
                                    handleClose={(shouldRefetch?: boolean) => {
                                      if (shouldRefetch) {
                                        refetchFieldWalkthroughDetail();
                                      }
                                      setIsShowingFloorPLanModal({
                                        [item.fieldWalkthroughItemId]: false
                                      });
                                    }}
                                    fieldWalkthroughId={fieldWalkthroughId}
                                    fieldWalkthroughItemId={item.fieldWalkthroughItemId}
                                    isLoadingDefaultFloorPlans={isLoadingDefaultFloorPlans}
                                    defaultFloorPlans={defaultFloorPlans}
                                    markupFiles={item.markupFiles}
                                    refetchFieldWalkthroughDetail={() =>
                                      refetchFieldWalkthroughDetail()
                                    }
                                  />
                                )}
                              </>
                            );
                          }}
                        </VariableSizeList>
                      );
                    }}
                  </AutoSizer>
                </TableBody>
              </MuiTable>
            </TableContainer>
          )}
          {!isFieldWalkthroughLoading && typeof selectedLocationId === 'number' && (
            <ButtonGroup sx={{ marginTop: '1rem' }}>
              <Button
                variant="contained"
                color="primary"
                disabled={isFieldWalkthroughLoading}
                onClick={() => {
                  if (hasItemsToAssign) {
                    showAssignToModal({
                      save: true
                    });
                  } else {
                    handleSave();
                  }
                }}
              >
                Save Changes
              </Button>
              {!isFieldWalkthroughLoading &&
                !isFirst &&
                selectedLocationId !== -1 &&
                selectedLocationId !== 1 && (
                  <Button
                    variant="contained"
                    color="secondary"
                    disabled={isFieldWalkthroughLoading}
                    onClick={() => {
                      if (hasItemsToAssign) {
                        showAssignToModal({
                          saveAndBack: true
                        });
                      } else {
                        handleSaveAndBack();
                      }
                    }}
                  >
                    Save and Go Back
                  </Button>
                )}
              {!isFieldWalkthroughLoading && !isLast && selectedLocationId !== -1 && (
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={isFieldWalkthroughLoading}
                  onClick={() => {
                    if (hasItemsToAssign) {
                      showAssignToModal({
                        saveAndContinue: true
                      });
                    } else {
                      handleSaveAndContinue();
                    }
                  }}
                >
                  Save and Continue
                </Button>
              )}
              {isAllowedToVerify &&
                !walkthrough?.hasOpenItems &&
                !isFieldWalkthroughLoading &&
                (isPM || isAdmin || isFieldAdmin) && (
                  <Button variant="contained" color="secondary" onClick={verifyComplete}>
                    Verify Complete
                  </Button>
                )}
            </ButtonGroup>
          )}
        </>
      )}

      <ImageModal
        isOpen={!!currentImage}
        onClose={() => setCurrentImage('')}
        source={currentImage}
      />

      <AssignToModal
        isOpen={
          isShowingAssignToModal?.punchlist ||
          isShowingAssignToModal?.save ||
          isShowingAssignToModal?.saveAndContinue ||
          isShowingAssignToModal?.saveAndBack ||
          false
        }
        handleClose={() => {
          showAssignToModal({});
        }}
        handleContinue={() => {
          isShowingAssignToModal?.punchlist && handleAddToPunchlist();
          isShowingAssignToModal?.save && handleSave();
          isShowingAssignToModal?.saveAndContinue && handleSaveAndContinue();
          isShowingAssignToModal?.saveAndBack && handleSaveAndBack();
        }}
        fieldWalkthrough={walkthrough}
        walkthroughItems={walkthroughItems}
        selectedWalkthroughItems={newItemsToAssign}
        setSelectedWalkthroughItems={
          items => setSelectedDeficientItems(items) // add to the selected deficient items
        }
        setWalkthroughItems={setWalkthroughItems}
        handleChangedItem={handleChangedItems}
      />
      <AddWalkthroughItemsModal
        open={isShowingAddItemsModal}
        onClose={shouldRefetch => {
          setIsShowingAddItemsModal(false);
          if (shouldRefetch) {
            showAssignToModal({
              punchlist: true
            });
            refetchFieldWalkthroughDetail();
            queryClient.invalidateQueries(['field-walkthrough-locations']);
          }
        }}
        fieldWalkthroughId={fieldWalkthroughId}
        setSelectedDeficientItems={
          (items: IFieldWalkthroughDetailItem[]) =>
            setSelectedDeficientItems(prev => [...prev, ...items]) // add to the selected deficient items
        }
      />
      <VerifyCompleteModal
        open={isShowingVerifyCompleteModal}
        onClose={shouldRefetch => {
          setIsShowingVerifyCompleteModal(false);
          if (shouldRefetch) {
            refetchFieldWalkthroughDetail();
            setSelectedDeficientItems([]);
            setChangedWalkthroughItems([]);
          }
        }}
        fieldWalkthroughId={fieldWalkthroughId}
        onSaving={setIsCompleting}
      />
    </Box>
  );
};

const useStyles = makeStyles<Theme>(theme => ({
  table: {
    height: '700px'
  },
  pictureImages: {
    userSelect: 'none',
    cursor: 'pointer',
    '&:hover::before': {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      color: theme.palette.common.white,
      content: '"See Larger"',
      fontSize: '0.85rem',
      position: 'absolute',
      textAlign: 'center',
      height: 50,
      width: 50,
      padding: 4
    }
  },
  colorImage: {
    width: 50,
    height: 50
  },
  label: {
    fontSize: '0.9rem'
  }
}));
